import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Button, TextInput, Icon } from "@zapier/design-system";
import { AiSparkle } from "../AiSparkle";
import styles from "./ChatInterface.module.css";

// Define the Message type for chat messages
export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "function" | "tool";
  content: string;
  name?: string;
  function_call?: any;
  tool_calls?: any[];
  tool_call_id?: string;
  toolInvocations?: any[];
}

interface ChatInterfaceProps {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
}

export function ChatInterface({
  onSubmit,
  isLoading = false,
}: ChatInterfaceProps) {
  const [messages] = useState<ChatMessage[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  return (
    <div className={styles.chatContainer}>
      <div ref={messagesContainerRef} className={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div className={styles.emptyChat}>
            <AiSparkle height={30} width={30} />
            <p>Ask me anything about the system</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.message} ${message.role === "user" ? styles.userMessage : styles.assistantMessage}`}
            >
              {message.role === "assistant" && (
                <div className={styles.messageIcon}>
                  <AiSparkle height={20} width={20} />
                </div>
              )}
              <div className={styles.messageContent}>{message.content}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className={styles.chatForm} onSubmit={onSubmit}>
        <TextInput
          ref={inputRef}
          name="message"
          placeholder="Ask me anything..."
          data-testid="chat-input"
        />
        <Button type="submit" disabled={isLoading}>
          <Icon name="miscAI" size={30} />
          Send
        </Button>
      </form>
    </div>
  );
}
